import * as React from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { OptimizelyProvider } from '@optimizely/react-sdk'
import { UserAttributes } from '@optimizely/optimizely-sdk'
import { Constants } from '@chegg/optimizely-sdk'
import optimizely from '@chegg-wt/core/state/optimizely/optimizely'
import { Site, Domain } from '@chegg-wt/core/types/site'
import { getOrSetVCookie } from '@chegg-wt/core/utils/getVCookie'
import { isProduction } from '@chegg-wt/core/utils/env'
import { getCookie, setCookie, Cookies, removeCookie } from '@chegg-wt/core/utils/cookies'
import { SNHU_SHOW_ONB_QUERYPARAM } from '@chegg-wt/core/constants/route'
import { UserRoles } from '@chegg-wt/core/constants/user'
import { userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { TEST_AUDIENCE } from '@chegg-wt/core/state/optimizely/constants'

export type UserInfo = {
  id: string
  attributes?: UserAttributes
}

interface OwnProps {
  cookieDomain: Domain
  siteName: Site
  children: React.ReactNode
}

const OptProvider: React.FunctionComponent<OwnProps> = ({
  children,
  siteName,
  cookieDomain,
}) => {
  const opt = optimizely(cookieDomain)
  const router = useRouter()

  const isSNHU = router.query[SNHU_SHOW_ONB_QUERYPARAM] === 'true'

  const storeUserRole = useSelector(userRoleSelector)
  const cookieUserRole = getCookie(Cookies.UserRole)
  const userRole = storeUserRole || cookieUserRole || UserRoles.NOT_LOGGED_IN

  if (!opt) {
    return <>{children}</>
  }

  let wtTestAudience = false
  if (router.query[TEST_AUDIENCE] === 'false') {
    removeCookie(Cookies.OptimizelyTestCookie)
  } else if (router.query[TEST_AUDIENCE] !== undefined) {
    wtTestAudience = true
    setCookie(Cookies.OptimizelyTestCookie, 'true', { expires: 1 })
  } else if (getCookie(Cookies.OptimizelyTestCookie) === 'true') {
    wtTestAudience = true
  }

  const user: UserInfo = {
    id: getOrSetVCookie(cookieDomain),
    attributes: {
      isSNHU,
      wtTestAudience,
      wtSiteName: siteName,
      wtUserRole: userRole,
      wtDeployEnv: !isProduction() ? Constants.DEV : Constants.PROD,
      // eslint-disable-next-line no-underscore-dangle
      wtCountryCode: (window as any).__sbmCountryCode || '',
    },
  }

  return (
    <OptimizelyProvider optimizely={opt} user={user} isServerSide={false}>
      {children}
    </OptimizelyProvider>
  )
}

export default OptProvider
